import axiosInstance from "@/lib/axiosInstance";
import type { CustomAxiosRequestConfig } from "@/types/common.types";
import { AUDIT_HISTORY_LIMIT, auditHistoryEndpoints } from "@/api/audit/auditHistory";
import type { AuditHistoryResponse, AuditLog } from "@/api/audit/auditHistory.types";
import { accessEndpoints } from "./access.endpoints";
import type { PermissionAction, SwitchRef, SwitchSource } from "./access.types";

export type AdjustmentEffect = "add" | "remove";

/** One row of `GET /access/users/:id/adjustments` (backend `AdjustmentView`). */
export interface Adjustment {
  id: string;
  userId: string;
  /** The company it counts in; a ServEngine one counts only at the ServEngine login. */
  tenantId: string;
  key: string;
  action: PermissionAction;
  effect: AdjustmentEffect;
  /** Calendar dates, `YYYY-MM-DD`. */
  reviewAt: string;
  endsOn: string | null;
  reason?: string;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  /** Past its end, the end of `endsOn` in New York. The engine no longer counts it. */
  ended: boolean;
  /** A live removal a live temporary role beats; the engine names that role. */
  overriddenBy: Extract<SwitchSource, { kind: "temporary_role" }> | null;
  /**
   * A ServEngine-level row listed in a company view (SERV-3578): it is changed from the main domain
   * only, so no write is offered on it here. Absent everywhere else.
   */
  readOnly?: boolean | null;
}

export interface AdjustmentWrite extends SwitchRef {
  tenantId: string;
  effect: AdjustmentEffect;
  reviewAt: string;
  endsOn?: string | null;
  reason?: string;
}

/** Only the dates and the reason change; the permission, company and effect never do. */
export type AdjustmentPatch = Partial<Pick<AdjustmentWrite, "reviewAt" | "endsOn" | "reason">>;

type AdjustmentsResponse = { data: Adjustment[] };
type AdjustmentResponse = { data: Adjustment };

/** Every adjustment in the person's companies this login may act in, ended ones included. */
export const fetchUserAdjustments = async (
  userId: string,
  signal?: AbortSignal,
): Promise<Adjustment[]> => {
  const { data } = await axiosInstance.get<AdjustmentsResponse>(
    accessEndpoints.adjustments(userId),
    { signal, suppressErrorAlert: true } as CustomAxiosRequestConfig,
  );
  return data.data;
};

/**
 * Acts in the company the body names, as the PATCH does, so the route's switch is asked there and
 * not in whatever the label menu has picked.
 */
export const addAdjustment = async (userId: string, write: AdjustmentWrite) => {
  const { data } = await axiosInstance.post<AdjustmentResponse>(
    accessEndpoints.adjustments(userId),
    write,
    { suppressErrorAlert: true, actingEntityId: write.tenantId } as CustomAxiosRequestConfig,
  );
  return data.data;
};

/** PATCH looks the row up in the request's company, so it acts in the adjustment's own. */
export const updateAdjustment = async (
  userId: string,
  adjustment: Pick<Adjustment, "id" | "tenantId">,
  patch: AdjustmentPatch,
) => {
  const { data } = await axiosInstance.patch<AdjustmentResponse>(
    `${accessEndpoints.adjustments(userId)}/${adjustment.id}`,
    patch,
    {
      suppressErrorAlert: true,
      actingEntityId: adjustment.tenantId,
    } as CustomAxiosRequestConfig,
  );
  return data.data;
};

/**
 * One adjustment's change log: its creation and every change of dates or note, oldest last. The
 * backend files it under the adjustment's own id and stamps it with the adjustment's company, and
 * `/audit/history` answers only for the company the request acts in, so it is read from there.
 */
export const fetchAdjustmentLog = async (
  adjustment: Pick<Adjustment, "id" | "tenantId">,
  signal?: AbortSignal,
): Promise<AuditLog[]> => {
  const { data } = await axiosInstance.get<AuditHistoryResponse>(auditHistoryEndpoints.history, {
    params: {
      resourceType: "user_access_adjustments",
      resourceId: adjustment.id,
      limit: AUDIT_HISTORY_LIMIT,
    },
    signal,
    suppressErrorAlert: true,
    actingEntityId: adjustment.tenantId,
  } as CustomAxiosRequestConfig);
  return data.data?.logs ?? [];
};
